/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Job } from '../../types/processing-schemas.ts';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { StatusRender } from './StatusRender.tsx';
import { ProgressRender } from './ProgressRender.tsx';
import { DateRender } from './DateRender.tsx';
import { DataTableColumns, statusColors } from '../TableHeader.tsx';
import { CopyText } from '../CopyText.tsx';

export const defaultJobsDatableColumns: DataTableColumns[] = [
  {
    order: 0,
    field: 'processID',
    header: 'Process ID',
    sortable: false
  },
  {
    order: 1,
    field: 'jobID',
    header: 'Job ID',
    body: ({ jobID }) => {
      return <CopyText text={jobID} />;
    }
  },
  {
    order: 2,
    field: 'status',
    header: 'Status',
    sortable: true,
    filter: true,
    filterMatchModeOptions: [
      { label: 'Equals', value: FilterMatchMode.EQUALS },
      { label: 'Not equals', value: FilterMatchMode.NOT_EQUALS }
    ],
    filterElement: (options) => {
      return (
        <Dropdown
          value={options.value}
          options={Object.keys(statusColors)}
          onChange={(e) => options.filterCallback(e.value, options.index)}
          itemTemplate={(value) => <StatusRender status={value} />}
          placeholder="Select One"
          className="p-column-filter"
          showClear
        />
      );
    },
    body: (data: Job) => <StatusRender status={data.status} />
  },
  {
    order: 3,
    field: 'created',
    header: 'Created',
    sortable: true,
    style: { width: '10%' },
    body: (data, { rowIndex }) => <DateRender key={rowIndex} job={data} showTooltip property="created" id={rowIndex} />
  },
  {
    order: 4,
    field: 'progress',
    header: 'Progress',
    align: 'center',
    sortable: true,
    body: ({ progress }: Job) => <ProgressRender progress={progress} />
  },
  {
    order: 5,
    field: 'message',
    header: 'Message',
    align: 'center',
    style: { width: '30%' },
    body: ({ message }: Job) => {
      if (message) {
        const length = 100;
        return message.length > length ? message.slice(0, length).concat('...') : message;
      }
      return null;
    }
  }
];
